/**
 * Một lần gọi tới API của mình, và **một** mã CSRF cho cả trang.
 *
 * Tách khỏi `accountService` khi endpoint báo lỗi xuất hiện, và lý do không phải là gọn gàng.
 * Mã CSRF là **trạng thái**: máy chủ phát nó ra ở một lần đọc và xoay nó mỗi lần đăng nhập hay
 * đăng xuất. Nếu mỗi service giữ một biến `csrf` riêng thì cái thứ hai sẽ đúng đúng một lần —
 * cho tới lần đăng nhập đầu tiên, sau đó nó cầm token cũ và mọi yêu cầu gửi đi đều bị từ chối
 * 403. Mà nó hỏng theo kiểu tệ nhất: hỏng **sau** khi đăng nhập thành công, nên trông như thể
 * việc đăng nhập mới là thứ bị hỏng.
 *
 * Một biến trong một module, dùng chung — đó là điều duy nhất khiến chuyện đó không xảy ra được.
 */

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

export interface Failure {
  readonly error: string;
}

export const isFailure = (value: unknown): value is Failure =>
  typeof value === 'object' && value !== null && 'error' in value;

/**
 * Mã hiện tại, giữ trong bộ nhớ.
 *
 * Không đọc từ `document.cookie`, dù nó nằm ở đó và đọc được là có chủ ý. Máy chủ trả mã mới
 * trong thân của mọi phản hồi có xoay mã, lấy từ đó thì hai bên không thể lệch nhau — còn đọc
 * cookie là một nguồn sự thật thứ hai, đúng gần như mọi lúc.
 */
let csrf: string | null = null;

export async function call<T>(
  path: string,
  init: { method?: string; body?: unknown } = {}
): Promise<T | Failure> {
  const method = init.method ?? (init.body === undefined ? 'GET' : 'POST');

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Thiếu dòng này thì cookie phiên không được gửi khi API nằm ở origin khác.
      credentials: 'include',
      headers: {
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(csrf === null ? {} : { 'x-csrf-token': csrf }),
      },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    return { error: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.' };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { error: `Máy chủ trả lời không đọc được (${String(response.status)}).` };
  }

  // Mọi phản hồi có xoay mã đều mang mã mới theo; lấy dù đó là phản hồi nào.
  const carried = (payload as { csrfToken?: unknown }).csrfToken;
  if (typeof carried === 'string') csrf = carried;

  if (!response.ok) {
    const message = (payload as { error?: unknown }).error;
    return { error: typeof message === 'string' ? message : 'Có lỗi xảy ra. Thử lại sau.' };
  }
  return payload as T;
}

/**
 * Đảm bảo trang đã có mã CSRF trước khi gửi thứ gì đó đi.
 *
 * Cần cho những chức năng **không** đi qua hộp thoại tài khoản. Mã được phát ra ở một lần `GET`,
 * và `GET /v1/me` là lần đọc mà ứng dụng vốn đã gọi lúc tải trang — nhưng "vốn đã" là một giả
 * định về một module khác, và giả định đó im lặng khi sai. Người dùng gặp lỗi ngay ở giây thứ ba,
 * bấm báo lỗi, và nhận về 403 mà chẳng hiểu vì sao: đó chính là báo cáo đáng giá nhất bị mất.
 */
export async function ensureCsrf(): Promise<void> {
  if (csrf !== null) return;
  await call('/v1/me');
}
